import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Icon, Loading } from '../../atoms'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import { TransportAndAccessProvider, useTransportAndAccess } from './TransportAndAccessContext'
import { TransportAndAccessOverview } from './components/TransportAndAccessOverview'
import { TransportAndAccessPublicTransport } from './components/TransportAndAccessPublicTransport'
import { TransportAndAccessEVCharging } from './components/TransportAndAccessEVCharging'
import {
  Container,
  Content,
  LoadingContainer,
  ErrorContainer,
} from './TransportAndAccessSection.styles'
import type { TransportServices } from './types'

export interface TransportAndAccessSectionProps {
  entity: {
    geo_center?: {
      lat: number
      lon: number
    }
  }
  services: TransportServices
  /** Callback to sync transport data to map context (used when not inside MapSectionProvider) */
  onMapDataChange?: (data: {
    publicTransportStops: any[]
    isochrones: Record<string, any> | null
    activeMobilityMetrics: any
    roadAccessMetrics: any
    services: TransportServices
  }) => void
}

interface TransportAndAccessContentProps {
  entity: {
    geo_center?: {
      lat: number
      lon: number
    }
  }
  onMapDataChange?: TransportAndAccessSectionProps['onMapDataChange']
}

const TransportAndAccessContent: React.FC<TransportAndAccessContentProps> = ({
  entity,
  onMapDataChange,
}) => {
  const { t } = useTranslation('transport')
  const {
    services,
    scores,
    activeView,
    setActiveView,
    setScores,
    setPublicTransportMetrics,
    setRoadAccessMetrics,
    setActiveMobilityMetrics,
    setIsochrones,
    setPublicTransportStops,
    setIsLoadingScores,
    setIsLoadingIsochrones,
    setIsLoadingStops,
    setError,
    error,
    isLoadingScores,
    publicTransportStops,
    isochrones,
    activeMobilityMetrics,
    roadAccessMetrics,
  } = useTransportAndAccess()

  // Fetch all transport data on mount or when entity changes
  useEffect(() => {
    if (!entity?.geo_center) return

    const fetchTransportData = async () => {
      const { lat, lon } = entity.geo_center as { lat: number; lon: number }

      setIsLoadingScores(true)
      setError(null)

      // Group 1: Fast calls — render tab content as soon as these resolve
      const metricsPromise = Promise.all([
        services.getPublicTransportMetrics(lat, lon),
        services.getRoadAccessMetrics(lat, lon),
        services.getActiveMobilityMetrics(lat, lon),
        services.getPublicTransportStops(lat, lon, 1500),
        services.getMultiModalIsochrones(lat, lon, [5, 10, 15, 30]),
      ]).then(([ptMetrics, roadMetrics, activeMetrics, ptStops, multiModalIsochrones]) => {
        setPublicTransportMetrics(ptMetrics)
        setRoadAccessMetrics(roadMetrics)
        setActiveMobilityMetrics(activeMetrics)
        setPublicTransportStops(ptStops)
        setIsochrones(multiModalIsochrones)
        setIsLoadingScores(false)
        setIsLoadingIsochrones(false)
        setIsLoadingStops(false)
      })

      // Group 2: Slow call — score badge updates when ready
      const scoresPromise = services.calculateTransportScores(lat, lon).then((transportScores) => {
        setScores(transportScores)
      })

      try {
        await Promise.all([metricsPromise, scoresPromise])
      } catch (err) {
        console.error('Error fetching transport data:', err)
        setError(t('states.error'))
        setIsLoadingScores(false)
        setIsLoadingIsochrones(false)
        setIsLoadingStops(false)
      }
    }

    fetchTransportData()
  }, [entity])

  // Sync transport data to map via callback whenever data changes
  useEffect(() => {
    if (!onMapDataChange) return

    if (publicTransportStops || isochrones || activeMobilityMetrics || roadAccessMetrics) {
      onMapDataChange({
        publicTransportStops,
        isochrones,
        activeMobilityMetrics,
        roadAccessMetrics,
        services,
      })
    }
  }, [
    publicTransportStops,
    isochrones,
    activeMobilityMetrics,
    roadAccessMetrics,
    onMapDataChange,
    services,
  ])

  // Get score variant for header
  const getScoreVariant = (score: number): 'success' | 'warning' | 'danger' | 'default' => {
    if (score >= 80) return 'success'
    if (score >= 60) return 'default'
    if (score >= 40) return 'warning'
    return 'danger'
  }

  const tabs = useMemo(
    () => [
      { id: 'overview', label: t('tabs.overview'), icon: 'layout-dashboard' },
      {
        id: 'public-transport',
        label: t('tabs.publicTransport'),
        icon: 'train',
      },
      { id: 'ev-charging', label: t('tabs.evCharging'), icon: 'zap' },
    ],
    [t]
  )

  return (
    <Container>
      <AnalysisSectionHeader
        title={t('header.title')}
        subtitle={t('header.subtitle')}
        score={
          scores
            ? {
                value: scores.overall,
                maxValue: 100,
                label: t('header.scoreLabel'),
                variant: getScoreVariant(scores.overall),
                showProgress: true,
              }
            : undefined
        }
      />

      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeView}
        onTabChange={(tabId) => setActiveView(tabId as any)}
      />

      <Content>
        {isLoadingScores ? (
          <LoadingContainer>
            <Loading size="large" />
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <Icon name="alert-triangle" size="small" /> {error}
          </ErrorContainer>
        ) : (
          <>
            {activeView === 'overview' && <TransportAndAccessOverview />}
            {activeView === 'public-transport' && <TransportAndAccessPublicTransport />}
            {activeView === 'ev-charging' && <TransportAndAccessEVCharging />}
          </>
        )}
      </Content>
    </Container>
  )
}

export const TransportAndAccessSection: React.FC<TransportAndAccessSectionProps> = ({
  entity,
  services,
  onMapDataChange,
}) => {
  return (
    <TransportAndAccessProvider services={services}>
      <TransportAndAccessContent entity={entity} onMapDataChange={onMapDataChange} />
    </TransportAndAccessProvider>
  )
}
